import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, BACKEND_URL } from "../../App";
import {
  Plus, Upload, Trash2, BookOpen, Play, Save, X, Eye, EyeOff
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogTitle, DialogHeader,
} from "../../components/ui/dialog";

export default function CoupleStoryManager() {
  const { wedding } = useOutletContext();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("Our Story");
  const [uploading, setUploading] = useState(false);
  const [editingPoint, setEditingPoint] = useState(null);
  const [pointForm, setPointForm] = useState({ timestamp: "", question: "", options: ["", ""], correct_answer: "" });
  const fileInputRef = useRef(null);

  const fetchStories = async () => {
    if (!wedding?.wedding_id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/stories/wedding/${wedding.wedding_id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setStories(res.data);
      if (res.data.length > 0 && !selectedStory) setSelectedStory(res.data[0]);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchStories(); }, [wedding]);

  const getMediaUrl = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/stories`, { wedding_id: wedding.wedding_id, title: newTitle },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Story created!");
      setCreateOpen(false);
      setNewTitle("Our Story");
      fetchStories();
      setSelectedStory(res.data);
    } catch (error) { toast.error("Failed to create story"); }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedStory) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/stories/${selectedStory.story_id}/video`, formData,
        { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` } });
      setSelectedStory(res.data);
      fetchStories();
      toast.success("Video uploaded!");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const handleTogglePublish = async () => {
    if (!selectedStory) return;
    try {
      const token = localStorage.getItem("token");
      const newStatus = selectedStory.status === "published" ? "draft" : "published";
      const res = await axios.put(`${API}/stories/${selectedStory.story_id}`,
        { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedStory(res.data);
      fetchStories();
      toast.success(newStatus === "published" ? "Story published!" : "Story unpublished");
    } catch { toast.error("Failed to update"); }
  };

  const handleAddPoint = async () => {
    if (!selectedStory || !pointForm.timestamp || !pointForm.question) return;
    const points = [...(selectedStory.interaction_points || [])];
    const newPoint = {
      timestamp: pointForm.timestamp,
      question: pointForm.question,
      options: pointForm.options.filter(o => o.trim()),
      correct_answer: pointForm.correct_answer,
      effect: "none"
    };
    if (editingPoint !== null) {
      points[editingPoint] = newPoint;
    } else {
      points.push(newPoint);
    }
    points.sort((a, b) => parseFloat(a.timestamp) - parseFloat(b.timestamp));
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${API}/stories/${selectedStory.story_id}`,
        { interaction_points: points }, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedStory(res.data);
      fetchStories();
      setPointForm({ timestamp: "", question: "", options: ["", ""], correct_answer: "" });
      setEditingPoint(null);
      toast.success(editingPoint !== null ? "Point updated!" : "Interaction point added!");
    } catch { toast.error("Failed to save"); }
  };

  const handleRemovePoint = async (index) => {
    const points = [...(selectedStory.interaction_points || [])];
    points.splice(index, 1);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.put(`${API}/stories/${selectedStory.story_id}`,
        { interaction_points: points }, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedStory(res.data);
      fetchStories();
      toast.success("Point removed");
    } catch { toast.error("Failed to remove"); }
  };

  const handleDeleteStory = async () => {
    if (!selectedStory || !window.confirm("Delete this story?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/stories/${selectedStory.story_id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setSelectedStory(null);
      fetchStories();
      toast.success("Story deleted");
    } catch { toast.error("Failed to delete"); }
  };

  if (!wedding) return <div className="text-wedding-ivory/60 text-center py-12">No wedding assigned.</div>;

  return (
    <div className="space-y-8" data-testid="couple-story-manager">
      <input ref={fileInputRef} type="file" accept="video/mp4" onChange={handleVideoUpload} className="hidden" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">Interactive Story</h1>
          <p className="text-wedding-ivory/60 mt-1">Create a 'How We Met' interactive experience</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="create-story-btn">
          <Plus className="w-4 h-4 mr-2" />New Story
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !selectedStory ? (
        <div className="text-center py-16 border border-dashed border-white/10">
          <BookOpen className="w-16 h-16 text-wedding-gold/20 mx-auto mb-4" />
          <p className="text-wedding-ivory/50">No stories yet. Create your interactive experience!</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Story Header */}
          <div className="p-6 bg-wedding-charcoal border border-white/5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl text-wedding-ivory">{selectedStory.title}</h2>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleTogglePublish}
                  className="bg-white/5 text-wedding-ivory/60 hover:bg-white/10 border border-white/10 text-xs">
                  {selectedStory.status === "published" ? <><EyeOff className="w-3 h-3 mr-1" />Unpublish</> : <><Eye className="w-3 h-3 mr-1" />Publish</>}
                </Button>
                <Button size="sm" onClick={handleDeleteStory}
                  className="bg-red-600/20 text-red-500 hover:bg-red-600/30 border border-red-500/30 text-xs">
                  <Trash2 className="w-3 h-3 mr-1" />Delete
                </Button>
              </div>
            </div>

            {/* Video */}
            {selectedStory.video_url ? (
              <div className="relative aspect-video bg-black rounded-sm overflow-hidden mb-4">
                <video src={getMediaUrl(selectedStory.video_url)} controls className="w-full h-full" data-testid="story-preview-video" />
              </div>
            ) : (
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="w-full aspect-video border border-dashed border-white/20 hover:border-wedding-gold/50 flex flex-col items-center justify-center gap-3 rounded-sm mb-4 transition-colors">
                {uploading ? <div className="w-8 h-8 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" /> : (
                  <><Upload className="w-8 h-8 text-wedding-ivory/40" /><span className="text-wedding-ivory/60 text-sm">Upload Story Video (MP4)</span></>
                )}
              </button>
            )}
          </div>

          {/* Interaction Points */}
          <div className="p-6 bg-wedding-charcoal border border-white/5">
            <h3 className="font-serif text-lg text-wedding-ivory mb-4">Interaction Points</h3>
            {(selectedStory.interaction_points || []).length === 0 ? (
              <p className="text-wedding-ivory/40 text-sm mb-4">No interaction points yet. Add questions that will pause the video.</p>
            ) : (
              <div className="space-y-3 mb-6">
                {selectedStory.interaction_points.map((point, i) => (
                  <div key={i} className="flex items-start justify-between p-4 bg-wedding-subtle border border-white/5">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-wedding-gold text-xs font-mono bg-wedding-gold/10 px-2 py-0.5 rounded">{point.timestamp}s</span>
                        <span className="text-wedding-ivory font-medium text-sm">{point.question}</span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {point.options?.map((opt, oi) => (
                          <span key={oi} className={`text-xs px-2 py-1 rounded ${opt === point.correct_answer ? "bg-green-500/20 text-green-400" : "bg-white/5 text-wedding-ivory/50"}`}>
                            {opt}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditingPoint(i); setPointForm({ ...point, options: point.options?.length >= 2 ? point.options : [...(point.options || []), ""] }); }}
                        className="p-1 text-wedding-ivory/40 hover:text-wedding-gold"><Save className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleRemovePoint(i)} className="p-1 text-wedding-ivory/40 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add/Edit Point Form */}
            <div className="p-4 border border-wedding-gold/20 bg-wedding-subtle space-y-4">
              <p className="text-wedding-gold text-sm font-medium">{editingPoint !== null ? "Edit Point" : "Add Interaction Point"}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-wedding-ivory/50 text-xs">Timestamp (seconds)</Label>
                  <Input type="number" step="0.1" value={pointForm.timestamp} onChange={(e) => setPointForm({ ...pointForm, timestamp: e.target.value })}
                    placeholder="e.g. 15.5" className="input-premium mt-1" data-testid="point-timestamp" />
                </div>
                <div>
                  <Label className="text-wedding-ivory/50 text-xs">Correct Answer</Label>
                  <Input value={pointForm.correct_answer} onChange={(e) => setPointForm({ ...pointForm, correct_answer: e.target.value })}
                    placeholder="Must match an option" className="input-premium mt-1" data-testid="point-correct-answer" />
                </div>
              </div>
              <div>
                <Label className="text-wedding-ivory/50 text-xs">Question</Label>
                <Input value={pointForm.question} onChange={(e) => setPointForm({ ...pointForm, question: e.target.value })}
                  placeholder="How did they meet?" className="input-premium mt-1" data-testid="point-question" />
              </div>
              <div>
                <Label className="text-wedding-ivory/50 text-xs">Options</Label>
                {pointForm.options.map((opt, i) => (
                  <div key={i} className="flex gap-2 mt-1">
                    <Input value={opt} onChange={(e) => {
                      const opts = [...pointForm.options]; opts[i] = e.target.value;
                      setPointForm({ ...pointForm, options: opts });
                    }} placeholder={`Option ${i + 1}`} className="input-premium" data-testid={`point-option-${i}`} />
                    {i >= 2 && <button onClick={() => { const opts = pointForm.options.filter((_, oi) => oi !== i); setPointForm({ ...pointForm, options: opts }); }}
                      className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>}
                  </div>
                ))}
                {pointForm.options.length < 4 && (
                  <button onClick={() => setPointForm({ ...pointForm, options: [...pointForm.options, ""] })}
                    className="text-wedding-gold text-xs mt-2 hover:underline">+ Add Option</button>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={handleAddPoint} className="btn-primary text-xs" disabled={!pointForm.timestamp || !pointForm.question}>
                  {editingPoint !== null ? "Update Point" : "Add Point"}
                </Button>
                {editingPoint !== null && (
                  <Button onClick={() => { setEditingPoint(null); setPointForm({ timestamp: "", question: "", options: ["", ""], correct_answer: "" }); }}
                    className="btn-secondary text-xs">Cancel</Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-wedding-charcoal border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">New Interactive Story</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <Label className="form-label">Story Title</Label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Our Story" className="input-premium mt-2" />
            </div>
            <div className="flex justify-end gap-3">
              <Button onClick={() => setCreateOpen(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleCreate} className="btn-primary" disabled={!newTitle}>Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
